import React from 'react';
import { Star } from 'lucide-react';

interface TestimonialProps {
  quote: string;
  name: string;
  role: string;
  hospital: string;
  rating: number;
  image: string;
}

const Testimonial: React.FC<TestimonialProps> = ({ quote, name, role, hospital, rating, image }) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-6 flex flex-col h-full">
      <div className="flex mb-4">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            size={16} 
            className={`${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} mr-1`} 
          />
        ))}
      </div>
      
      <p className="text-gray-600 italic mb-6">{quote}</p>
      
      <div className="mt-auto flex items-center">
        <img 
          src={image} 
          alt={name} 
          className="w-12 h-12 rounded-full mr-4 object-cover" 
        />
        <div>
          <p className="font-medium">{name}</p>
          <p className="text-sm text-gray-500">{role}, {hospital}</p>
        </div>
      </div>
    </div>
  );
};

const Testimonials: React.FC = () => {
  const testimonials = [
    {
      quote: "Since implementing this system, our SPD efficiency has increased by 40%. The real-time tracking has eliminated missing instruments and reduced delays in the OR significantly.",
      name: "Dr. Sarah Johnson",
      role: "Chief of Surgery",
      hospital: "Metro General Hospital",
      rating: 5,
      image: "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    {
      quote: "The automated documentation has been a game-changer for regulatory compliance. What used to take our team hours now happens automatically with perfect accuracy.",
      name: "Michael Rodriguez",
      role: "SPD Manager",
      hospital: "Valley Medical Center",
      rating: 5,
      image: "https://images.pexels.com/photos/5490276/pexels-photo-5490276.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    {
      quote: "We've seen a 94% reduction in processing errors and nearly eliminated surgical delays related to instrument availability. The ROI has exceeded our expectations.",
      name: "Jennifer Chang",
      role: "Director of Perioperative Services",
      hospital: "University Hospital",
      rating: 5,
      image: "https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    }
  ];

  return (
    <section id="testimonials" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">What Our Clients Say</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Hear from healthcare professionals who have transformed their operations with our automated system.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Testimonial
              key={index}
              quote={testimonial.quote}
              name={testimonial.name}
              role={testimonial.role}
              hospital={testimonial.hospital}
              rating={testimonial.rating}
              image={testimonial.image}
            />
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <div className="inline-flex items-center justify-center p-1 rounded-full bg-gradient-to-r from-[#2e609b] to-[#1ba63f]">
            <div className="bg-white px-8 py-3 rounded-full">
              <p className="font-medium">
                <span className="text-[#2e609b]">100+ healthcare facilities</span> trust our system nationwide
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;